import type { FormStatusCountItem, GetFormStatusCountsResponse } from '#services/api/insights.ts';

import { Box, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import {
  ChartsTooltipContainer,
  Unstable_FunnelChart as FunnelChart,
  funnelSectionClasses,
  useItemTooltip,
  useSeries,
} from '@mui/x-charts-pro';
import { useQuery } from '@tanstack/react-query';

import { formStatusCounts } from '#lib/react-query/queries.ts';
import { ChartsTooltip } from '#routes/Home/Widgets/ChartsTooltip.tsx';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import { CHART_SERIES_COLORS, WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from './Base';
import { EmptyDataOverlay } from './EmptyDataOverlay.tsx';
import { useFormWidget } from './use-form-widget.ts';

/**
 * By default, when a value in the series is `0`, the section is not plotted.
 * This value is a workaround to always show a section even when this happens.
 */
const PAD = 20;

const widgetTitle = 'Form Completion Journey';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Displays a funnel chart showing the number of forms at each stage of the completion process—
        <InfoEmphasis>assigned</InfoEmphasis>,<InfoEmphasis>opened</InfoEmphasis>,
        <InfoEmphasis>started</InfoEmphasis>,<InfoEmphasis>completed</InfoEmphasis>, and&nbsp;
        <InfoEmphasis>reviewed</InfoEmphasis>. This helps identify where forms are abandoned, where
        adoption is high, and where bottlenecks occur in patient engagement workflows.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection
        title="Count of forms currently in each status (each form is counted once in its latest status):"
        indented
      >
        <InfoList
          items={[
            <>
              <InfoEmphasis>Assigned:</InfoEmphasis>
              &nbsp;Form has been assigned but not yet opened
            </>,
            <>
              <InfoEmphasis>Opened:</InfoEmphasis>
              &nbsp;Form has been opened but not yet started
            </>,
            <>
              <InfoEmphasis>Started:</InfoEmphasis>
              &nbsp;Form has been started but not yet completed
            </>,
            <>
              <InfoEmphasis>Completed:</InfoEmphasis>
              &nbsp;Form has been completed but not yet reviewed
            </>,
            <>
              <InfoEmphasis>Reviewed:</InfoEmphasis>
              &nbsp;Form has been reviewed
            </>,
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Forms with a valid status representing their current stage in the journey',
            'Forms not marked as deleted or canceled',
            'Forms matching the selected date range and filters',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList items={['Deleted forms', 'Canceled forms']} />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            <>
              Each form appears <InfoEmphasis>in only one stage</InfoEmphasis>
              —its most advanced completed stage
            </>,
            'Stages are mutually exclusive; totals across stages do not sum to total assigned forms if some forms have been removed or filtered out',
            <>
              The funnel reflects a <InfoEmphasis>snapshot&nbsp;</InfoEmphasis> of current statuses
              as of the data extraction date, not historical transitions
            </>,
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

/**
 * Post-processes the response from the form status counts used to
 * memoize to prevent executing the function on every render.
 */
function postProcessResponse(response: GetFormStatusCountsResponse) {
  // Make any necessary data transformations on the response here
  return {
    ...response,
    data: response.data
      .slice()
      .reverse()
      .reduce(
        (acc, cur, i) => {
          // Since we accumulate the count, we only need to pad the first time
          const previousCount = acc[i - 1]?.value ?? PAD;
          acc.push({
            ...cur,
            label: cur.status.charAt(0).toUpperCase() + cur.status.slice(1),
            value: previousCount + cur.count,
            labelMarkType: 'circle',
          });
          return acc;
        },
        [] as (FormStatusCountItem & {
          value: number;
          label: string;
          labelMarkType: 'square' | 'circle' | 'line';
        })[]
      )
      .reverse(),
  };
}

export function FormCompletionJourney() {
  const widget = useFormWidget();

  // Fetch form trends data
  const { data, isLoading } = useQuery({
    ...formStatusCounts(widget.getCommonRequestParams()),
    select: postProcessResponse,
  });

  /**
   * Even though we can read the mode from the callback signature of the
   * `colors` prop, we need to access it here to calculate the proper color
   * contrast for the label based on the mode. The two are always in sync.
   */
  const theme = useTheme();

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  const colors = CHART_SERIES_COLORS[theme.palette.mode];

  const funnelChartSx = colors.reduce(
    (acc, color, index) => ({
      ...acc,
      [`& .${funnelSectionClasses.label}:nth-of-type(${index + 1})`]: {
        fill: theme.palette.getContrastText(
          data?.data[index]?.value !== PAD
            ? color
            : /**
               * When the value is `0`, the section is plotted with a
               * transparent background, so we need to calculate the contrast
               * based on the element behind it.
               */
              theme.palette.background.paper
        ),
      },
      ...(data?.data[index]?.value === PAD && {
        [`& .${funnelSectionClasses.root}:nth-of-type(${index + 1})`]: {
          fill: 'transparent',
          stroke: color,
          strokeWidth: '1px',
        },
      }),
    }),
    {} as Record<string, { fill: string }>
  );

  return (
    <Base
      title={widgetTitle}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    >
      {(isOpenedInDialog) => (
        <Box height="100%">
          {data?.data[0] && data.data[0].count > 0 ? (
            <FunnelChart
              colors={colors}
              series={[
                {
                  id: 'journey',
                  data: data.data,
                  valueFormatter: (item) => `${item.value - PAD}`,
                },
              ]}
              height={isOpenedInDialog ? undefined : WIDGET_CHART_HEIGHT}
              slots={{
                tooltip: CustomTooltip,
              }}
              sx={funnelChartSx}
            />
          ) : (
            <EmptyDataOverlay variant={isOpenedInDialog ? 'dialog' : 'chart'} />
          )}
        </Box>
      )}
    </Base>
  );
}

// TODO: Need to standardize a base custom tooltip to be used across all charts
function CustomTooltip() {
  const tooltipData = useItemTooltip();
  // TODO: There is currently a PR open to expose useFunnelSeries which once
  //       merged and we've updated our lib version we should use instead
  //       https://github.com/mui/mui-x/pull/18034#issuecomment-2913884262
  const fullData = useSeries();

  const seriesId = tooltipData?.identifier.seriesId ?? '';
  const dataIndex = tooltipData?.identifier.dataIndex ?? 0;
  const seriesData = fullData.funnel?.series[seriesId]?.data ?? [];
  const item = seriesData[dataIndex];
  const prev = seriesData[dataIndex - 1];

  // For tooltip placement to work correctly, we ALWAYS need to return
  // an instance of ChartsTooltipContainer
  if (!tooltipData || !item) {
    return <ChartsTooltipContainer trigger="item" />;
  }

  const itemLabel = typeof item.label === 'function' ? item.label('tooltip') : item.label;
  const prevLabel = typeof prev?.label === 'function' ? prev.label('tooltip') : prev?.label;
  const itemRealValue = item.value - PAD;
  const prevRealValue = prev ? prev.value - PAD : undefined;

  return (
    <ChartsTooltipContainer trigger="item">
      <ChartsTooltip title={itemLabel} seriesColor={tooltipData.color}>
        <Stack direction="column" padding={1} spacing={1}>
          <Typography color="common.white" component="p" fontSize={12} variant="body2">
            Count: {itemRealValue}
          </Typography>
          {itemRealValue > 0 && (
            <Typography color="common.white" component="p" fontSize={12} variant="body2">
              Percentage:{' '}
              {prevRealValue === undefined
                ? `100%`
                : `${((itemRealValue / prevRealValue) * 100).toFixed(1)}% of ${prevLabel}`}
            </Typography>
          )}
          {/*
            We use a ternary to avoid a common rendering error when the value
            is `0` by doing `prevRealValue &&`.
          */}
          {prevRealValue ? (
            <Typography color="common.white" component="p" fontSize={12} variant="body2">
              Drop-off from {prevLabel}:{' '}
              {`${(100 - (itemRealValue / prevRealValue) * 100).toFixed(1)}%`}
            </Typography>
          ) : null}
        </Stack>
      </ChartsTooltip>
    </ChartsTooltipContainer>
  );
}
